import { useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  EmptyDataTable,
  SearchForm,
  SearchFormProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableRowWithLink,
  IconButton,
} from '@occc/ui-design-system';
import { useGetLocationGroupsByUuids } from '@occc-app/dataAccessLayer/locationGroup/queries';
import { routeCreator as locationRouteCreator } from '@occc-app/routes/Location/utils';

type GroupsTableProps = {
  groupIds: string[];
  title: string;
  onClose?: () => void;
  tableSx?: SxProps;
};

export const GroupsTable = ({ groupIds, title, onClose, tableSx }: GroupsTableProps) => {
  const [search, setSearch] = useState('');

  const { data: groups, loading, error } = useGetLocationGroupsByUuids(groupIds);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    if (!search?.trim()) return groups;

    const q = search.trim().toLowerCase();
    return groups.filter((group) => {
      const uuid = group.uuid.toLowerCase();
      const name = group.name.toLowerCase();
      return name.includes(q) || uuid.includes(q);
    });
  }, [groups, search]);

  const handleSearchSubmit = useCallback<SearchFormProps['onSubmit']>(({ search }) => {
    setSearch(search ?? '');
  }, []);

  if (loading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' width='30vw' sx={tableSx}>
        <CircularProgress size={24} color='primary' />
      </Box>
    );
  }

  return (
    <TableContainer elevation={0} square>
      <TableHeader
        title={title}
        actions={
          <Box display='flex' alignItems='center' gap={1}>
            <SearchForm onSubmit={handleSearchSubmit} initialValues={{ search }} />
            {onClose && (
              <IconButton onClick={onClose} size='medium' aria-label='Close dialog'>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            )}
          </Box>
        }
      />
      {error ? (
        <Box sx={tableSx}>
          <EmptyDataTable />
        </Box>
      ) : (
        <Box sx={tableSx}>
          <Table>
            <TableHead>
              <TableRow hover={false}>
                <TableCell>Group Name</TableCell>
                <TableCell>Group ID</TableCell>
                <TableCell align='right'>Locations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.length === 0 ? (
                <TableRow hover={false}>
                  <TableCell colSpan={3} align='center'>
                    {groups?.length === 0 ? 'No groups' : 'No matching groups'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group) => (
                  <TableRowWithLink
                    key={group.uuid}
                    to={locationRouteCreator.locationGroupDetails(group.uuid)}
                  >
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.uuid}</TableCell>
                    <TableCell align='right'>{group.countOfLocations || 0}</TableCell>
                  </TableRowWithLink>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </TableContainer>
  );
};
